/**
 * Country/Language geolocation suggestion popup.
 *
 * Uses Shopify's browsing_context_suggestions.json endpoint to detect
 * whether a visitor's browser/IP points to a better-matching market or
 * language than the one they're currently browsing in. Per Shopify's own
 * best-practice guidance, this NEVER redirects automatically — it only
 * suggests, and the visitor always confirms (or picks manually, or dismisses).
 * https://shopify.dev/docs/storefronts/themes/markets/localization-discovery
 *
 * Debugging: append ?geo_popup_preview=1 to any storefront URL to force the
 * popup open with a demo suggestion, regardless of real geolocation or the
 * dismissed-cookie, so it can be reviewed without VPN/IP tricks.
 */
(function () {
  'use strict';

  var COOKIE_NAME = 'geo_locale_popup_dismissed';

  var STRINGS = {
    de: {
      country: 'Wir haben erkannt, dass du dich in {country} befindest.',
      language: 'Möchtest du auf {language} fortfahren?',
      countryAndLanguage: 'Wir haben erkannt, dass du dich in {country} befindest. Auf {language} fortfahren?',
      confirmCountry: 'Ja, zu {country}',
      confirmLanguage: 'Ja, auf {language}',
      confirmBoth: 'Ja, fortfahren',
      stay: 'Hier bleiben',
      toggleManual: 'Land oder Sprache manuell wählen',
      toggleManualClose: 'Auswahl schließen',
    },
    en: {
      country: "We noticed you're located in {country}.",
      language: 'Would you like to continue in {language}?',
      countryAndLanguage: "We noticed you're located in {country}. Continue in {language}?",
      confirmCountry: 'Yes, go to {country}',
      confirmLanguage: 'Yes, switch to {language}',
      confirmBoth: 'Yes, continue',
      stay: 'Stay here',
      toggleManual: 'Choose country or language manually',
      toggleManualClose: 'Close selection',
    },
    nl: {
      country: 'We hebben gemerkt dat je je in {country} bevindt.',
      language: 'Wil je doorgaan in het {language}?',
      countryAndLanguage: 'We hebben gemerkt dat je je in {country} bevindt. Doorgaan in het {language}?',
      confirmCountry: 'Ja, naar {country}',
      confirmLanguage: 'Ja, in het {language}',
      confirmBoth: 'Ja, doorgaan',
      stay: 'Hier blijven',
      toggleManual: 'Land of taal handmatig kiezen',
      toggleManualClose: 'Selectie sluiten',
    },
  };

  function getStrings() {
    var lang = (document.documentElement.lang || 'de').slice(0, 2).toLowerCase();
    return STRINGS[lang] || STRINGS.de;
  }

  function format(template, values) {
    return template.replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/; SameSite=Lax';
  }

  function init() {
    var root = document.getElementById('GeoLocalePopup');
    if (!root) return;

    var params = new URLSearchParams(window.location.search);
    var isPreview = params.get('geo_popup_preview') === '1';

    if (!isPreview) {
      if (getCookie(COOKIE_NAME)) return;
      if (window.Shopify && Shopify.designMode) return;
    }

    var currentCountry = (window.Shopify && Shopify.country) || root.dataset.currentCountry;
    var currentLanguage = (window.Shopify && Shopify.language) || root.dataset.currentLanguage;
    var rememberDays = parseInt(root.dataset.rememberDays, 10) || 30;

    var messageEl = root.querySelector('[data-geo-popup-message]');
    var confirmBtn = root.querySelector('[data-geo-popup-confirm]');
    var stayBtn = root.querySelector('.geo-locale-popup__stay');
    var toggleManualBtn = root.querySelector('[data-geo-popup-toggle-manual]');
    var manualPanel = root.querySelector('[data-geo-popup-manual]');
    var countryCodeInput = document.getElementById('GeoLocalePopupCountryCode');
    var languageCodeInput = document.getElementById('GeoLocalePopupLanguageCode');
    var form = document.getElementById('GeoLocalePopupForm');

    function submitForm(countryCode, languageCode) {
      if (countryCodeInput) countryCodeInput.value = countryCode;
      if (languageCodeInput) languageCodeInput.value = languageCode;
      setCookie(COOKIE_NAME, '1', rememberDays);
      if (form) form.submit();
    }

    function dismiss() {
      setCookie(COOKIE_NAME, '1', rememberDays);
      root.classList.remove('is-visible');
      window.setTimeout(function () {
        root.hidden = true;
      }, 320);
      document.removeEventListener('keydown', onKeydown);
    }

    function onKeydown(event) {
      if (event.key === 'Escape') dismiss();
    }

    function show(suggestedCountry, suggestedLanguage) {
      var strings = getStrings();
      var hasCountry = suggestedCountry && suggestedCountry.handle !== currentCountry;
      var hasLanguage = suggestedLanguage && suggestedLanguage.handle !== currentLanguage;

      if (!hasCountry && !hasLanguage) return;

      var values = {
        country: suggestedCountry ? suggestedCountry.name : '',
        language: suggestedLanguage ? suggestedLanguage.name : '',
      };

      var messageText;
      var confirmText;
      if (hasCountry && hasLanguage) {
        messageText = format(strings.countryAndLanguage, values);
        confirmText = format(strings.confirmBoth, values);
      } else if (hasCountry) {
        messageText = format(strings.country, values);
        confirmText = format(strings.confirmCountry, values);
      } else {
        messageText = format(strings.language, values);
        confirmText = format(strings.confirmLanguage, values);
      }

      if (messageEl) messageEl.textContent = messageText;
      if (confirmBtn) confirmBtn.textContent = confirmText;
      if (stayBtn) stayBtn.textContent = strings.stay;
      if (toggleManualBtn) toggleManualBtn.textContent = strings.toggleManual;

      var finalCountryCode = suggestedCountry ? suggestedCountry.handle : currentCountry;
      var finalLanguageCode = suggestedLanguage ? suggestedLanguage.handle : currentLanguage;

      if (confirmBtn) {
        confirmBtn.addEventListener('click', function () {
          submitForm(finalCountryCode, finalLanguageCode);
        });
      }

      root.hidden = false;
      // Force reflow so the transition runs.
      // eslint-disable-next-line no-unused-expressions
      root.offsetHeight;
      window.requestAnimationFrame(function () {
        root.classList.add('is-visible');
      });
      document.addEventListener('keydown', onKeydown);
    }

    root.querySelectorAll('[data-geo-popup-dismiss]').forEach(function (el) {
      el.addEventListener('click', dismiss);
    });

    if (toggleManualBtn && manualPanel) {
      toggleManualBtn.addEventListener('click', function () {
        var isOpen = !manualPanel.hidden;
        manualPanel.hidden = isOpen;
        toggleManualBtn.setAttribute('aria-expanded', String(!isOpen));
        var strings = getStrings();
        toggleManualBtn.textContent = isOpen ? strings.toggleManual : strings.toggleManualClose;
      });
    }

    root.querySelectorAll('[data-geo-popup-set-country]').forEach(function (el) {
      el.addEventListener('click', function () {
        submitForm(el.getAttribute('data-geo-popup-set-country'), currentLanguage);
      });
    });

    root.querySelectorAll('[data-geo-popup-set-language]').forEach(function (el) {
      el.addEventListener('click', function () {
        submitForm(currentCountry, el.getAttribute('data-geo-popup-set-language'));
      });
    });

    if (isPreview) {
      // Demo data for visual/functional review — no real geolocation call
      // needed. Deliberately suggests the Netherlands (the only market with
      // its own domain, nescon-shop.nl) together with English, so confirming
      // actually exercises the real cross-domain + language redirect instead
      // of just showing placeholder copy.
      show(
        currentCountry !== 'NL' ? { handle: 'NL', name: 'Niederlande' } : { handle: 'DE', name: 'Deutschland' },
        currentLanguage !== 'en' ? { handle: 'en', name: 'English' } : { handle: 'de', name: 'Deutsch' }
      );
      return;
    }

    var root_url = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var endpoint =
      root_url +
      'browsing_context_suggestions.json' +
      '?country[enabled]=true' +
      '&country[exclude]=' +
      encodeURIComponent(currentCountry || '') +
      '&language[enabled]=true' +
      '&language[exclude]=' +
      encodeURIComponent(currentLanguage || '');

    fetch(endpoint, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        if (!data || !data.suggestions || !data.suggestions.length) return;
        var parts = data.suggestions[0].parts || {};
        show(parts.country, parts.language);
      })
      .catch(function () {
        /* Fail silently — the popup simply won't show. */
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
